
import React, { Fragment, useEffect, useRef, useState } from 'react';

// Components

import SummaryItem from '../SummaryItem';
import MaskUnmaskIcon from '../MaskUnmaskData';

// Action Creators

import { receiveImmigrationInfo } from '@actions/ssap/ssapActionCreators';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { toUTCAndFormat } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { MASK_TIMEOUT, maskValue } from '@utils/miscellaneous/mask.utils';

// Constants

import { ELIGIBILITY_DOCUMENTS } from '@constants/ssap/hhmTypes/documentsOfEligibility';
import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';


const CitizenshipImmigrationStatusInfo = props => {

    const timeoutRef = useRef(null);
    const [imiMasked, setImiMasked] = useState({
        alienNumber: true,
        sevisId: true,
        foreignPassportOrDocumentNumber: true,
        i94Number: true,
        visaNumber: true
    });

    const {

        applyingForCoverageIndicator,
        citizenshipImmigrationStatus: {
            citizenshipStatusIndicator,
            dateOfEntry,
            eligibleImmigrationDocumentType,
            hasI864Sponsor,
            naturalizedCitizenshipIndicator,
            lawfulPresenceIndicator,
            livedIntheUSSince1996Indicator,
            citizenshipDocument: {
                alienNumber,
                sevisId,
                nameOnDocument,
                documentDescription,
                nameSameOnDocumentIndicator,
                visaNumber,
                documentExpirationDate,
                i94Number,
                foreignPassportOrDocumentNumber,
                foreignPassportCountryOfIssuance,
                cardNumber
            },
            otherImmigrationDocumentType: {
                orrcertificationIndicator,
                cubanHaitianEntrantIndicator,
                orreligibilityLetterIndicator,
                stayOfRemovalIndicator,
                americanSamoanIndicator,
                domesticViolence,
                indianOfUsOrCanada,
                withholdingOfRemovalIndicator,
                deportationWithheldInaIndicator,
                refugeeIndicator,
                specialIraqiAfghaniImmigrantIndicator,
                victimSevereTraffickingIndicator,
                conditionalEntrantIndicator,
                lawfulPermanentResidentIndicator,
                paroledAtLeastOneYearIndicator,
                temporaryNonimmigrantIndicator,
                deferredActionChildhoodArrivalsIndicator,
                amerasianNoncitizenIndicator,
                asyleeIndicator,
                cofaMigrantIndicator,
                otherIndicator
            },
            hasRefugeeStatus,
            unchangedImmigrationStatusLastFiveYears
        },
        personName,
        isState,
        idx
    } = props;

    if(!applyingForCoverageIndicator) {
        return null;
    }

    const getDocumentName = (documentType = [{ }]) => {
        const [ documentName = null ] = ELIGIBILITY_DOCUMENTS
            .filter(doc => documentType[0][doc.name]).map(doc => doc.text);
        return documentName;
    };

    const getDateFormatted = (date, isPrevious) => {
        if(date === null) {
            return isPrevious ? NO_PRIOR_DATA : date;
        }
        return toUTCAndFormat(date, MM_SLASH_DD_SLASH_YYYY);
    };

    const getValue = (val, key) => {
        return imiMasked[key] ? maskValue(val) : val;
    };

    const handleMaskToggle = (key, masked) => {
        setImiMasked(prev => ({ ...prev, [key]: masked }));

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (!masked) {
            // Set a new timeout to mask the data after 15 seconds
            timeoutRef.current = setTimeout(() => {
                setImiMasked(prev => ({ ...prev, [key]: true }));
                timeoutRef.current = null;
            }, MASK_TIMEOUT);
        }
    };

    const maskUnmaskIconProps = (key) => ({
        receiveData: receiveImmigrationInfo,
        masked: imiMasked[key],
        setMasked: (masked) => handleMaskToggle(key, masked) 
    });

    useEffect(() => {
        // Cleanup timeout on component unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const getPersonName = (name, isPrevious = false) => {
        if ((name.firstName === null || name.lastName === null) && isPrevious) {
            return NO_PRIOR_DATA;
        }

        return normalizeNameOfPerson(name);
    };

    return (

        <Fragment>
            <SummaryItem
                textCode = 'common.are_you_us_citizen'
                value = { citizenshipStatusIndicator }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipStatusIndicator`
                        ]
                    ]
                }
            />

            <SummaryItem
                textCode = 'common.are_you_naturalized_citizen'
                value = { naturalizedCitizenshipIndicator }
                noPriorDataValues = { [ null ] }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.naturalizedCitizenshipIndicator`
                        ]
                    ]
                }
            />

            {
                naturalizedCitizenshipIndicator

                    ? (
                        <Fragment>
                            <SummaryItem
                                textCode = 'common.alien_number'
                                value={getValue(alienNumber, 'alienNumber')}
                                icon={<MaskUnmaskIcon {...maskUnmaskIconProps('alienNumber')} />}
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.alienNumber`,
                                            getValue,
                                            ['alienNumber']
                                        ]
                                    ]
                                }
                            />

                            <SummaryItem
                                textCode = 'common.naturalization_number'
                                value={getValue(sevisId, 'sevisId')}
                                icon={<MaskUnmaskIcon {...maskUnmaskIconProps('sevisId')} />}
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.sevisId`,
                                            getValue,
                                            ['sevisId']
                                        ]
                                    ]
                                }
                            />
                        </Fragment>
                    )
                    : null
            }

            {
                !citizenshipStatusIndicator &&

                <SummaryItem
                    textCode = 'common.immigration_status'
                    value = { lawfulPresenceIndicator }
                    desc = {
                        [
                            [
                                `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.lawfulPresenceIndicator`
                            ]
                        ]
                    }
                />
            }
            {
                lawfulPresenceIndicator &&

                <Fragment>
                    {
                        getDocumentName(eligibleImmigrationDocumentType) &&

                        <SummaryItem
                            textCode = { getDocumentName(eligibleImmigrationDocumentType) }
                            value = 'common.no_text'
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.eligibleImmigrationDocumentType`,
                                        getDocumentName
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        alienNumber &&

                        <SummaryItem
                            textCode = 'common.alien_number'
                            value={getValue(alienNumber, 'alienNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('alienNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.alienNumber`,
                                        getValue,
                                        ['alienNumber']
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        sevisId &&

                        <SummaryItem
                            textCode = 'common.naturalization_number'
                            value={getValue(sevisId, 'sevisId')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('sevisId')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.sevisId`,
                                        getValue,
                                        ['sevisId']
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        documentDescription &&

                        <SummaryItem
                            textCode = 'common.document_description'
                            value = { documentDescription }
                            ignoreIntl
                            noPriorDataValues = { [ null, '' ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.documentDescription`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        visaNumber &&

                        <SummaryItem
                            textCode = 'common.visa_number'
                            value={getValue(visaNumber, 'visaNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('visaNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.visaNumber`,
                                        getValue,
                                        ['visaNumber']
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        foreignPassportOrDocumentNumber &&

                        <SummaryItem
                            textCode = 'common.foreign_passport_number'
                            value={getValue(foreignPassportOrDocumentNumber, 'foreignPassportOrDocumentNumber')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('foreignPassportOrDocumentNumber')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.foreignPassportOrDocumentNumber`,
                                        getValue,
                                        ['foreignPassportOrDocumentNumber']

                                    ]
                                ]
                            }
                        />
                    }
                    {
                        foreignPassportCountryOfIssuance &&

                        <SummaryItem
                            textCode = 'common.foreign_county_issuance'
                            value = { foreignPassportCountryOfIssuance }
                            ignoreIntl
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.foreignPassportCountryOfIssuance`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        documentExpirationDate &&

                        <SummaryItem
                            textCode = 'common.document_expiration_date'
                            value={getDateFormatted(documentExpirationDate)}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.documentExpirationDate`,
                                        getDateFormatted,
                                        [ true ]
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        i94Number &&

                        <SummaryItem
                            textCode = 'common.i94_number'
                            value={getValue(i94Number, 'i94Number')}
                            icon={<MaskUnmaskIcon {...maskUnmaskIconProps('i94Number')} />}
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.citizenshipDocument.i94Number`,
                                        getValue,
                                        ['i94Number']
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        cardNumber &&

                        <SummaryItem
                            textCode = 'common.card_number'
                            value = { cardNumber }
                            ignoreIntl
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.cardNumber`
                                    ]
                                ]
                            }
                        />

                    }
                    {
                        orrcertificationIndicator &&

                        <SummaryItem
                            textCode = 'common.orr_certification'
                            value = { orrcertificationIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.orrcertificationIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        cubanHaitianEntrantIndicator &&

                        <SummaryItem
                            textCode = 'common.cuban_haitian'
                            value = { cubanHaitianEntrantIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.cubanHaitianEntrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        orreligibilityLetterIndicator &&

                        <SummaryItem
                            textCode = 'common.refugee_resettlement'
                            value = { orreligibilityLetterIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.orreligibilityLetterIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        stayOfRemovalIndicator &&

                        <SummaryItem
                            textCode = 'common.staying_removal_issued'
                            value = { stayOfRemovalIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.stayOfRemovalIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        americanSamoanIndicator &&

                        <SummaryItem
                            textCode = 'common.resident_of_american_samoa'
                            value = { americanSamoanIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.americanSamoanIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        domesticViolence &&
                        <SummaryItem
                            textCode='ssap.input.label.domestic_violence'
                            value={domesticViolence}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.domesticViolence`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        indianOfUsOrCanada &&
                        <SummaryItem
                            textCode='ssap.input.label.indian_of_us_or_canada'
                            value={indianOfUsOrCanada}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.indianOfUsOrCanada`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        withholdingOfRemovalIndicator &&

                        <SummaryItem
                            textCode = 'common.withholding_removal'
                            value = { withholdingOfRemovalIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.otherImmigrationDocumentType.withholdingOfRemovalIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        deportationWithheldInaIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.deportation_withheld_ina'
                            value={deportationWithheldInaIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.deportationWithheldInaIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        refugeeIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.refugee'
                            value={refugeeIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.refugeeIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        specialIraqiAfghaniImmigrantIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.special_iraqi_afghani_immigrant'
                            value={specialIraqiAfghaniImmigrantIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.specialIraqiAfghaniImmigrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        victimSevereTraffickingIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.victim_severe_trafficking'
                            value={victimSevereTraffickingIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.victimSevereTraffickingIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        conditionalEntrantIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.conditional_entrant'
                            value={conditionalEntrantIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.conditionalEntrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        lawfulPermanentResidentIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.lawful_permanent_resident'
                            value={lawfulPermanentResidentIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.lawfulPermanentResidentIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        isState.MN && lawfulPermanentResidentIndicator && hasRefugeeStatus !== null &&

                            <SummaryItem
                                textCode='ssap.input.legend.mn_has_refugee_status'
                                replacements={{ personName }}
                                value={hasRefugeeStatus}
                                desc={
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.hasRefugeeStatus`
                                        ]
                                    ]
                                }
                            />
                    }
                    {
                        paroledAtLeastOneYearIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.paroled_at_least_one_year'
                            value={paroledAtLeastOneYearIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.paroledAtLeastOneYearIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        temporaryNonimmigrantIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.temporary_nonimmigrant'
                            value={temporaryNonimmigrantIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.temporaryNonimmigrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        deferredActionChildhoodArrivalsIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.deferred_action_childhood_arrivals'
                            value={deferredActionChildhoodArrivalsIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.deferredActionChildhoodArrivalsIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        amerasianNoncitizenIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.amerasian_noncitizen'
                            value={amerasianNoncitizenIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.amerasianNoncitizenIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        asyleeIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.asylee'
                            value={asyleeIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.asyleeIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        cofaMigrantIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.cofa_migrant'
                            value={cofaMigrantIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.cofaMigrantIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        otherIndicator &&
                        <SummaryItem
                            textCode='ssap.input.label.other_status'
                            value={otherIndicator}
                            desc={
                                [
                                    [
                                        `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.otherImmigrationDocumentType.otherIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        nameSameOnDocumentIndicator !== null &&

                        <SummaryItem
                            textCode = 'common.name_same_on_document'
                            value = { nameSameOnDocumentIndicator }
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.nameSameOnDocumentIndicator`
                                    ]
                                ]
                            }
                        />
                    }
                    {
                        nameSameOnDocumentIndicator !== null && !nameSameOnDocumentIndicator &&

                        <SummaryItem
                            textCode = 'common.name_as_shown_on_document'
                            value = { getPersonName(nameOnDocument) }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.citizenshipDocument.nameOnDocument`,
                                        getPersonName,
                                        [ true ]
                                    ]
                                ]
                            }
                        />

                    }

                    <SummaryItem
                        textCode = 'common.residence_since_1996'
                        replacements = { { personName } }
                        value = { livedIntheUSSince1996Indicator }
                        noPriorDataValues = { [ null ] }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.livedIntheUSSince1996Indicator`
                                ]
                            ]
                        }
                    />

                    {
                        unchangedImmigrationStatusLastFiveYears !== null &&

                            <SummaryItem
                                textCode = 'common.has_current_status_five_last_years'
                                value = { unchangedImmigrationStatusLastFiveYears }
                                noPriorDataValues = { [ null ] }
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].citizenshipImmigrationStatus.unchangedImmigrationStatusLastFiveYears`
                                        ]
                                    ]
                                }
                            />

                    }

                    {
                        isState.MN &&
                            <Fragment>
                                <SummaryItem
                                    textCode='ssap.input.legend.date_Of_Entry'
                                    value={getDateFormatted(dateOfEntry)}
                                    desc={
                                        [
                                            [
                                                `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.dateOfEntry`,
                                                getDateFormatted,
                                                [ true ]
                                            ]
                                        ]
                                    }
                                />
                                <SummaryItem
                                    textCode='ssap.input.legend.has_I864_Sponsor'
                                    replacements={{ personName }}
                                    value={hasI864Sponsor}
                                    noPriorDataValues = { [ null ] }
                                    desc={
                                        [
                                            [
                                                `taxHousehold.householdMember[${idx}].citizenshipImmigrationStatus.hasI864Sponsor`
                                            ]
                                        ]
                                    }
                                />
                            </Fragment>
                    }
                </Fragment>
            }
        </Fragment>
    );
};

export default CitizenshipImmigrationStatusInfo;